import * as VueRuntime from 'vue/dist/vue.esm-bundler.js';
import { createEventBus } from 'js/utils/eventBus';

const installedPlugins = [];

const legacyConfig = {
    warnHandler: null,
    devtools: true
};

function isCoreVuetifyPlugin(plugin) {
    return Boolean(
        plugin &&
            typeof plugin.install === 'function' &&
            plugin.defaults &&
            plugin.display &&
            plugin.theme &&
            plugin.icons
    );
}

export function applyLegacyPlugins(app) {
    const seenPlugins = new Set();
    installedPlugins.forEach(({ plugin, options }) => {
        if (!plugin || seenPlugins.has(plugin) || isCoreVuetifyPlugin(plugin)) {
            return;
        }
        seenPlugins.add(plugin);
        try {
            app.use(plugin, ...options);
        } catch (error) {
            // Skip legacy Vue 2 plugins that expect Vue.prototype.
            const message =
                error && (error.message || (typeof error === 'string' ? error : ''));
            if (
                typeof message === 'string' &&
                (message.includes('is not a constructor') ||
                    message.includes('Cannot read properties of undefined'))
            ) {
                return;
            }
            // eslint-disable-next-line no-console
            console.warn('Skipping incompatible legacy plugin:', error);
        }
    });
}

function prepareRootOptions(options = {}) {
    if (typeof options.data === 'function' || typeof options.data === 'undefined') {
        return options;
    }
    return {
        ...options,
        data() {
            return options.data;
        }
    };
}

function VueCompat(options = {}) {
    if (!options || Object.keys(options).length === 0) {
        return createEventBus();
    }

    const normalizedOptions = prepareRootOptions(options);
    const app = VueRuntime.createApp(normalizedOptions);

    app.config.compilerOptions.delimiters = normalizedOptions.delimiters || [
        '[[',
        ']]'
    ];

    if (typeof legacyConfig.warnHandler === 'function') {
        app.config.warnHandler = legacyConfig.warnHandler;
    }

    // Vue 2 instance API compatibility for templates/plugins still emitting
    // this.$set/this.$delete during migration.
    app.config.globalProperties.$set = (target, key, value) => {
        target[key] = value;
        return value;
    };
    app.config.globalProperties.$delete = (target, key) => {
        delete target[key];
    };

    applyLegacyPlugins(app);

    if (normalizedOptions.router) {
        app.use(normalizedOptions.router);
    }
    if (normalizedOptions.vuetify) {
        app.use(normalizedOptions.vuetify);
    }

    const mountTarget = normalizedOptions.el || '#MainApp';
    if (mountTarget) {
        app.mount(mountTarget);
    }

    return app;
}

VueCompat.config = legacyConfig;
VueCompat.use = (plugin, ...options) => {
    installedPlugins.push({ plugin, options });
    return VueCompat;
};
VueCompat.set = (target, key, value) => {
    target[key] = value;
    return value;
};
VueCompat.delete = (target, key) => {
    delete target[key];
};

Object.assign(VueCompat, VueRuntime);

export default VueCompat;
export * from 'vue/dist/vue.esm-bundler.js';
